import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, FileText, Image, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { cn } from '../lib/utils';
import { CreateExpenseClaimDto, ExpenseClaim, expenseClaimsService } from '../services/expenseClaimsService';

// ─── Types ────────────────────────────────────────────────────────────────────

type Category = CreateExpenseClaimDto['category'];

/** One uploaded file with its own date, amount and category */
type FileRow = {
  name: string;
  fileUrl: string;
  mimeType: string;
  size: number;
  // per-file fields
  date: string;
  amount: string;
  category: Category;
};

export type ExpenseClaimDialogProps = {
  open: boolean;
  editingClaim: ExpenseClaim | null;
  loading?: boolean;
  /** In create mode an array of payloads (one per file) is passed. Edit mode passes a single payload. */
  onSave: (
    payloads: CreateExpenseClaimDto | CreateExpenseClaimDto[],
    submit: boolean,
  ) => Promise<void>;
  onClose: () => void;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const categoryOptions: Array<{ value: Category; label: string }> = [
  { value: 'MEAL', label: 'Pasto' },
  { value: 'TRANSPORT', label: 'Trasporto' },
  { value: 'ACCOMMODATION', label: 'Alloggio' },
  { value: 'OTHER', label: 'Altro' },
];

const getToday = () => new Date().toISOString().split('T')[0];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileIcon = ({ mimeType }: { mimeType: string }) =>
  mimeType.startsWith('image/') ? (
    <Image className="h-4 w-4 shrink-0 text-blue-500" />
  ) : (
    <FileText className="h-4 w-4 shrink-0 text-amber-500" />
  );

const CategorySelect = ({
  value,
  onChange,
}: {
  value: Category;
  onChange: (v: Category) => void;
}) => {
  const { t } = useTranslation();
  return (
    <Select value={value} onValueChange={(v) => onChange(v as Category)}>
      <SelectTrigger className="h-8 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {categoryOptions.map((c) => (
          <SelectItem key={c.value} value={c.value}>
            {t(`expenseClaims.categories.${c.value.toLowerCase()}`, c.label)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// ─── Dialog ───────────────────────────────────────────────────────────────────

export const ExpenseClaimDialog: React.FC<ExpenseClaimDialogProps> = ({
  open,
  editingClaim,
  loading = false,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // ── Edit mode single-form state ───────────────────────────────────────────

  const initialEdit = useMemo(() => ({
    date: editingClaim?.date?.slice(0, 10) ?? getToday(),
    category: (editingClaim?.category ?? 'MEAL') as Category,
    amount: String(editingClaim?.amount ?? ''),
    hasReceipt: editingClaim?.hasReceipt ?? true,
    declaredWithoutReceipt: editingClaim?.declaredWithoutReceipt ?? false,
    notes: editingClaim?.notes ?? '',
    existingFiles: (editingClaim?.attachments ?? []).map((a) => ({
      name: a.fileUrl.split('/').pop() ?? 'allegato',
      fileUrl: a.fileUrl,
      mimeType: a.mimeType,
      size: 0,
    })),
  }), [editingClaim]);

  const [editForm, setEditForm] = useState(initialEdit);

  // ── Create mode per-file rows ─────────────────────────────────────────────

  const [fileRows, setFileRows] = useState<FileRow[]>([]);
  const [globalNotes, setGlobalNotes] = useState('');

  // Fallback (no files) form
  const [noFileForm, setNoFileForm] = useState({
    date: getToday(),
    category: 'OTHER' as Category,
    amount: '',
    declaredWithoutReceipt: false,
    notes: '',
  });

  // Reset all state when dialog opens/closes or switching between edit/create
  React.useEffect(() => {
    setEditForm(initialEdit);
    setFileRows([]);
    setGlobalNotes('');
    setNoFileForm({ date: getToday(), category: 'OTHER', amount: '', declaredWithoutReceipt: false, notes: '' });
    setIsDragOver(false);
    setUploadingFiles(false);
  }, [initialEdit, open]);

  // ── File processing ───────────────────────────────────────────────────────

  const addFiles = useCallback(async (files: File[]) => {
    setUploadingFiles(true);
    const today = getToday();
    try {
      const processed = await Promise.all(
        files.map(async (file) => {
          const uploaded = await expenseClaimsService.uploadAttachment(file);
          return {
            name: file.name,
            fileUrl: uploaded.fileUrl,
            mimeType: uploaded.mimeType || file.type || 'application/octet-stream',
            size: file.size,
            date: today,
            amount: '',
            category: 'OTHER' as Category,
          };
        }),
      );
      setFileRows((prev) => [...prev, ...processed]);
    } finally {
      setUploadingFiles(false);
    }
  }, []);

  const updateRow = (index: number, patch: Partial<FileRow>) =>
    setFileRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const removeRow = (index: number) =>
    setFileRows((prev) => prev.filter((_, i) => i !== index));

  // ── Drop zone handlers ────────────────────────────────────────────────────

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length) await addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      await addFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  // ── Validation ────────────────────────────────────────────────────────────

  const createValid =
    fileRows.length > 0
      ? fileRows.every((r) => r.date && r.amount && Number(r.amount) > 0)
      : Number(noFileForm.amount) > 0 && noFileForm.declaredWithoutReceipt;

  const editValid =
    Number(editForm.amount) > 0 &&
    (editForm.hasReceipt || editForm.declaredWithoutReceipt);

  // ── Submit helpers ───────────────────────────────────────────────────────

  const buildPayloads = (): CreateExpenseClaimDto | CreateExpenseClaimDto[] => {
    if (editingClaim) {
      return {
        date: editForm.date,
        category: editForm.category,
        amount: Number(editForm.amount),
        hasReceipt: editForm.hasReceipt,
        declaredWithoutReceipt: editForm.hasReceipt ? false : editForm.declaredWithoutReceipt,
        notes: editForm.notes.trim() || undefined,
        attachments: editForm.existingFiles.map((f) => ({ fileUrl: f.fileUrl, mimeType: f.mimeType })),
      };
    }

    if (fileRows.length > 0) {
      return fileRows.map((r) => ({
        date: r.date,
        category: r.category,
        amount: Number(r.amount),
        hasReceipt: true,
        declaredWithoutReceipt: false,
        notes: globalNotes.trim() || undefined,
        attachments: [{ fileUrl: r.fileUrl, mimeType: r.mimeType }],
      }));
    }

    return {
      date: noFileForm.date,
      category: noFileForm.category,
      amount: Number(noFileForm.amount),
      hasReceipt: false,
      declaredWithoutReceipt: noFileForm.declaredWithoutReceipt,
      notes: noFileForm.notes.trim() || undefined,
      attachments: [],
    };
  };

  const handleSaveDraft = async (e?: React.FormEvent) => {
    e?.preventDefault();
    await onSave(buildPayloads(), false);
  };

  const handleSubmitRequest = async () => {
    await onSave(buildPayloads(), true);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingClaim
              ? t('expenseClaims.form.editTitle', 'Modifica nota spese')
              : t('expenseClaims.form.newTitle', 'Nuova nota spese')}
          </DialogTitle>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSaveDraft}>

          {/* ─────────────────── EDIT MODE ─────────────────────────── */}
          {editingClaim && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ec-date">{t('expenseClaims.form.date', 'Data')}</Label>
                  <Input id="ec-date" type="date" value={editForm.date}
                    onChange={(e) => setEditForm((p) => ({ ...p, date: e.target.value }))} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ec-amount">{t('expenseClaims.form.amount', 'Importo (EUR)')}</Label>
                  <Input id="ec-amount" type="number" step="0.01" min="0" placeholder="0.00"
                    value={editForm.amount}
                    onChange={(e) => setEditForm((p) => ({ ...p, amount: e.target.value }))} required />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>{t('expenseClaims.form.category', 'Categoria')}</Label>
                <CategorySelect value={editForm.category} onChange={(v) => setEditForm((p) => ({ ...p, category: v }))} />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox id="ec-has-receipt" checked={editForm.hasReceipt}
                    onCheckedChange={(c) => setEditForm((p) => ({ ...p, hasReceipt: Boolean(c), declaredWithoutReceipt: c ? false : p.declaredWithoutReceipt }))} />
                  <Label htmlFor="ec-has-receipt">{t('expenseClaims.form.receiptAttached', 'Scontrino allegato')}</Label>
                </div>
                {!editForm.hasReceipt && (
                  <div className="flex items-center gap-2">
                    <Checkbox id="ec-declared" checked={editForm.declaredWithoutReceipt}
                      onCheckedChange={(c) => setEditForm((p) => ({ ...p, declaredWithoutReceipt: Boolean(c) }))} />
                    <Label htmlFor="ec-declared" className="text-sm">
                      {t('expenseClaims.form.declaration', 'Dichiaro che questa spesa è tracciabile senza ricevuta')}
                    </Label>
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ec-notes">{t('expenseClaims.form.notes', 'Note')}</Label>
                <Textarea id="ec-notes" rows={2} value={editForm.notes}
                  onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
                  placeholder={t('expenseClaims.form.notesPlaceholder', 'Dettagli facoltativi...')} />
              </div>

              {editForm.existingFiles.length > 0 && (
                <div className="space-y-1.5">
                  <Label>{t('expenseClaims.form.attachments', 'Allegati')}</Label>
                  {editForm.existingFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 rounded-md border px-3 py-2 bg-muted/30">
                      <FileIcon mimeType={f.mimeType} />
                      <p className="flex-1 text-sm truncate">{f.name}</p>
                      <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive"
                        onClick={() => setEditForm((p) => ({ ...p, existingFiles: p.existingFiles.filter((_, j) => j !== i) }))}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ─────────────────── CREATE MODE ───────────────────────── */}
          {!editingClaim && (
            <>
              {/* Drop zone */}
              <div
                className={cn(
                  'rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors',
                  isDragOver
                    ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/20'
                    : 'border-muted-foreground/25 hover:border-muted-foreground/50 hover:bg-muted/30',
                )}
                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input ref={fileInputRef} type="file" multiple accept="image/*,application/pdf"
                  className="hidden" onChange={handleFileInput} />
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium">
                  {t('expenseClaims.form.dropFilesHere', 'Trascina i file qui oppure clicca per selezionarli')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('expenseClaims.form.supportedFormats', 'Immagini e PDF — più file supportati')}
                </p>
                {uploadingFiles && (
                  <div className="mt-2 inline-flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {t('expenseClaims.form.uploadingFiles', 'Caricamento file su archivio...')}
                  </div>
                )}
              </div>

              {/* Per-file rows — each with date, amount, category */}
              {fileRows.length > 0 && (
                <div className="space-y-2">
                  {/* Header */}
                  <div className="grid grid-cols-[24px_1fr_100px_100px_140px_28px] gap-2 px-1 text-xs text-muted-foreground font-medium">
                    <span />
                    <span>{t('expenseClaims.form.file', 'File')}</span>
                    <span>{t('expenseClaims.form.date', 'Data')}</span>
                    <span>{t('expenseClaims.form.amount', 'Importo')}</span>
                    <span>{t('expenseClaims.form.category', 'Categoria')}</span>
                    <span />
                  </div>

                  {fileRows.map((row, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-[24px_1fr_100px_100px_140px_28px] gap-2 items-center rounded-md border px-2 py-1.5 bg-muted/20"
                    >
                      <FileIcon mimeType={row.mimeType} />

                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate">{row.name}</p>
                        {row.size > 0 && (
                          <p className="text-[10px] text-muted-foreground">{formatFileSize(row.size)}</p>
                        )}
                      </div>

                      <Input
                        type="date"
                        value={row.date}
                        onChange={(e) => updateRow(i, { date: e.target.value })}
                        className="h-8 text-xs px-2"
                        required
                      />

                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={row.amount}
                        onChange={(e) => updateRow(i, { amount: e.target.value })}
                        className="h-8 text-xs px-2"
                        required
                      />

                      <CategorySelect value={row.category} onChange={(v) => updateRow(i, { category: v })} />

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                        onClick={() => removeRow(i)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}

                  {/* Shared note for all rows */}
                  <div className="space-y-1.5 pt-1">
                    <Label htmlFor="ec-global-notes">
                      {t('expenseClaims.form.notes', 'Note comuni (opzionale)')}
                    </Label>
                    <Textarea
                      id="ec-global-notes"
                      value={globalNotes}
                      onChange={(e) => setGlobalNotes(e.target.value)}
                      placeholder={t('expenseClaims.form.notesPlaceholder', 'Dettagli facoltativi...')}
                      rows={2}
                    />
                  </div>
                </div>
              )}

              {/* No-file fallback */}
              {fileRows.length === 0 && (
                <div className="rounded-lg border p-4 space-y-3 bg-muted/20">
                  <p className="text-xs text-muted-foreground">
                    {t('expenseClaims.form.noFileHint', 'Nessun file? Puoi comunque registrare la spesa compilando il modulo.')}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="ec-nr-date">{t('expenseClaims.form.date', 'Data')}</Label>
                      <Input id="ec-nr-date" type="date" value={noFileForm.date}
                        onChange={(e) => setNoFileForm((p) => ({ ...p, date: e.target.value }))} required />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="ec-nr-amount">{t('expenseClaims.form.amount', 'Importo (EUR)')}</Label>
                      <Input id="ec-nr-amount" type="number" step="0.01" min="0" placeholder="0.00"
                        value={noFileForm.amount}
                        onChange={(e) => setNoFileForm((p) => ({ ...p, amount: e.target.value }))} />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t('expenseClaims.form.category', 'Categoria')}</Label>
                    <CategorySelect value={noFileForm.category} onChange={(v) => setNoFileForm((p) => ({ ...p, category: v }))} />
                  </div>
                  <div className="flex items-start gap-2">
                    <Checkbox id="ec-nr-declared" checked={noFileForm.declaredWithoutReceipt}
                      onCheckedChange={(c) => setNoFileForm((p) => ({ ...p, declaredWithoutReceipt: Boolean(c) }))}
                      className="mt-0.5" />
                    <Label htmlFor="ec-nr-declared" className="text-sm leading-snug">
                      {t('expenseClaims.form.declaration', 'Dichiaro che questa spesa è tracciabile senza ricevuta')}
                    </Label>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ec-nr-notes">{t('expenseClaims.form.notes', 'Note')}</Label>
                    <Textarea id="ec-nr-notes" rows={2} value={noFileForm.notes}
                      onChange={(e) => setNoFileForm((p) => ({ ...p, notes: e.target.value }))}
                      placeholder={t('expenseClaims.form.notesPlaceholder', 'Dettagli facoltativi...')} />
                  </div>
                </div>
              )}
            </>
          )}

          {!editingClaim && (
            <p className="text-xs text-muted-foreground">
              {t('expenseClaims.form.submitWarning', 'L\'invio mette la nota spese in approvazione e non può essere annullato.')}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              {t('common.cancel', 'Annulla')}
            </Button>

            {editingClaim ? (
              <Button
                type="submit"
                disabled={loading || uploadingFiles || !editValid}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('common.saving', 'Salvataggio...')}
                  </>
                ) : (
                  t('expenseClaims.form.update', 'Salva modifiche')
                )}
              </Button>
            ) : (
              <>
                <Button
                  type="submit"
                  variant="outline"
                  disabled={loading || uploadingFiles || !createValid}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('common.saving', 'Salvataggio...')}
                    </>
                  ) : (
                    t('expenseClaims.form.saveDraft', 'Salva bozza')
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={handleSubmitRequest}
                  disabled={loading || uploadingFiles || !createValid}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('common.saving', 'Salvataggio...')}
                    </>
                  ) : (
                    t('expenseClaims.form.submitRequest', 'Invia richiesta')
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
